import React, {useState} from 'react';
import {Button, Tag, Space} from 'antd';
import {PlusOutlined, SearchOutlined, ClearOutlined, DownOutlined, UpOutlined} from '@ant-design/icons';
import {
    AppliedFilter,
    IDefaultLabels,
    SearchCriteriaConfig
} from "./type/search-criteria.type";
import set = Reflect.set;

export interface SearchCriteriaViewProps {
    title: string;
    subtitle?: string;
    className?: string;
    showMoreFilters: boolean;
    setShowMoreFilters: React.Dispatch<React.SetStateAction<boolean>>;
    defaultLabels: IDefaultLabels;
    availableCriteria: SearchCriteriaConfig[];
    renderInputForCriteria?: (criteria: SearchCriteriaConfig) => React.ReactNode;
    appliedFilters: AppliedFilter[];
    removeFilter: (id: string) => void;
    clearAllFilters: () => void;
    handleSearch: () => void;
    isSearchDisabled: boolean;
    showReset?: boolean;
    renderTopContent?: () => React.ReactNode;
}

const SearchCriteriaView = (props: SearchCriteriaViewProps) => {
    const [collapsed, setCollapsed] = useState(true);
    const handleToggleCollapse = () => {
        setCollapsed(!collapsed);
        props.setShowMoreFilters(false);
    }

    return (
        <div className={`common-search-wrapper ${props.className || ''}`}>
            <div className="search-container">
                <div className="search-header" onClick={handleToggleCollapse}>
                    <h3>{props.title}</h3>
                    {collapsed ? (
                        <Button className={'collapsed-button'} onClick={() => setCollapsed(false)}>
                            <DownOutlined/>
                        </Button>
                    ) : (
                        <Button className={'collapsed-button'} onClick={() => setCollapsed(true)}>
                            <UpOutlined/>
                        </Button>
                    )}
                </div>
                {
                    !collapsed && (
                        <div className="search-content">
                            {props.renderTopContent && (
                                <div className="custom-top-section">
                                    {props.renderTopContent()}
                                    {props.showMoreFilters && (
                                        <div className="more-filters-section">
                                            {props.availableCriteria?.map(criteria => (
                                                <div key={criteria.key} className="more-filter-item">
                                                    <label className="more-filter-label">
                                                        {criteria.label}
                                                    </label>
                                                    <div className="more-filter-input">
                                                        {props.renderInputForCriteria?.(criteria)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="criteria-actions">
                                {
                                    props.availableCriteria.length > 0 && !props.showMoreFilters && (
                                        <Button
                                            className={'more-filters-button'}
                                            icon={<PlusOutlined/>}
                                            onClick={() => props.setShowMoreFilters(v => !v)}
                                        >
                                            More Filters
                                        </Button>
                                    )
                                }
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined/>}
                                    onClick={props.handleSearch}
                                    disabled={props.isSearchDisabled}
                                    size="large"
                                    className={"search-reset-action-button"}
                                >
                                    {props.defaultLabels.searchButton}
                                </Button>

                                {props.showReset && (
                                    <Button
                                        icon={<ClearOutlined/>}
                                        onClick={props.clearAllFilters}
                                        disabled={props.isSearchDisabled}
                                        size="large"
                                        className={"search-reset-action-button"}
                                    >
                                        {props.defaultLabels.resetButton}
                                    </Button>
                                )}

                            </div>

                            {props.appliedFilters.length > 0 && (
                                <div className="applied-filters-section">
                                    <div className="filters-header">
                                        <h3>{props.defaultLabels.appliedFilters}</h3>
                                        <a onClick={props.clearAllFilters}>
                                            {props.defaultLabels.clearAll}
                                        </a>
                                    </div>

                                    <Space wrap>
                                        {props.appliedFilters.map(filter => (
                                            <Tag
                                                key={filter.id}
                                                closable
                                                onClose={() => props.removeFilter(filter.id)}
                                            >
                                                <strong>{filter.label}:</strong> {filter.displayValue}
                                            </Tag>
                                        ))}
                                    </Space>
                                </div>
                            )}

                        </div>
                    )
                }

            </div>
        </div>
    );
};

export default SearchCriteriaView;
